import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BarChart3, Target, Shield, Zap, TrendingUp, Globe, ArrowRight } from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1762279389020-eeeb69c25813?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHw0fHxmaW5hbmNpYWwlMjBncmFwaHxlbnwwfHx8fDE3NzYyMTUyOTF8MA&ixlib=rb-4.1.0&q=85";
const FEATURE_IMG_1 = "https://images.unsplash.com/photo-1634542984003-e0fb8e200e91?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxmaW5hbmNpYWwlMjBncmFwaHxlbnwwfHx8fDE3NzYyMTUyOTF8MA&ixlib=rb-4.1.0&q=85";
const FEATURE_IMG_2 = "https://images.unsplash.com/photo-1638481826540-7710b13f7d53?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwyfHxmaW5hbmNpYWwlMjBncmFwaHxlbnwwfHx8fDE3NzYyMTUyOTF8MA&ixlib=rb-4.1.0&q=85";

const features = [
    { icon: Target, title: "Precise Entries", desc: "AI-powered entry points with optimal risk/reward ratios" },
    { icon: Shield, title: "Stop Loss Levels", desc: "Calculated protection levels based on market structure" },
    { icon: Zap, title: "Take Profit Targets", desc: "Multiple TP levels for scaling out of positions" },
    { icon: TrendingUp, title: "Drop Any Chart", desc: "Upload a screenshot from any platform and get analysis in seconds" },
    { icon: Globe, title: "All Markets", desc: "Stocks, Forex, Crypto, and Futures analysis" },
    { icon: BarChart3, title: "AI Technical Analysis", desc: "GPT-5.2 powered market analysis engine" },
];

export default function LandingPage() {
    return (
        <div className="min-h-screen bg-[#0A0A0A]">
            {/* Nav */}
            <header className="glass-header fixed top-0 left-0 right-0 z-50 border-b border-white/10">
                <div className="max-w-7xl mx-auto flex items-center justify-between h-14 px-6">
                    <div className="flex items-center gap-2">
                        <BarChart3 className="w-6 h-6 text-[#007AFF]" />
                        <span className="text-lg font-bold tracking-tight text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>TradeNalysis</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <Link to="/login">
                            <Button variant="ghost" className="text-zinc-400 hover:text-white rounded-full text-sm" data-testid="landing-login-btn">
                                Login
                            </Button>
                        </Link>
                        <Link to="/register">
                            <Button className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-full px-5 text-sm font-bold" data-testid="landing-register-btn">
                                Start Free Trial
                            </Button>
                        </Link>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-14" data-testid="hero-section">
                <div className="absolute inset-0">
                    <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-30" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/80 to-transparent" />
                </div>
                <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
                    <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#007AFF] mb-6 animate-fadeIn">
                        AI-Powered Trading Analysis
                    </p>
                    <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-white mb-6 animate-fadeIn stagger-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        Trade Smarter.<br />Not Harder.
                    </h1>
                    <p className="text-base md:text-lg text-zinc-400 max-w-2xl mx-auto mb-10 leading-relaxed animate-fadeIn stagger-2">
                        Get precise entry points, stop losses, and take profit targets powered by GPT-5.2.
                        Analyze stocks, forex, crypto, and futures in seconds.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fadeIn stagger-3">
                        <Link to="/register">
                            <Button className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-full px-8 py-6 text-base font-bold animate-pulse-glow" data-testid="hero-cta-btn">
                                Start 3-Day Free Trial <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </Link>
                        <p className="text-xs text-zinc-500">No credit card required</p>
                    </div>
                </div>
            </section>

            {/* Features */}
            <section className="py-24 px-6" data-testid="features-section">
                <div className="max-w-6xl mx-auto">
                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-3 text-center">Features</p>
                    <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white text-center mb-16" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        Everything You Need to Trade
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
                        {features.map((f, i) => (
                            <div key={i} className="border border-white/10 rounded-2xl p-8 hover:bg-white/[0.02] transition-all" data-testid={`feature-card-${i}`}>
                                <f.icon className="w-6 h-6 text-[#007AFF] mb-4" />
                                <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>{f.title}</h3>
                                <p className="text-sm text-zinc-400 leading-relaxed">{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Showcase */}
            <section className="py-24 px-6 border-t border-white/10">
                <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="border border-white/10 rounded-2xl overflow-hidden">
                        <img src={FEATURE_IMG_1} alt="Real-time trading charts" className="w-full h-64 object-cover opacity-80" />
                        <div className="p-6">
                            <h3 className="text-xl font-semibold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>Drop Any Chart, Get Analysis</h3>
                            <p className="text-sm text-zinc-400">Screenshot any chart from TradingView or your favorite platform and get AI analysis in seconds.</p>
                        </div>
                    </div>
                    <div className="border border-white/10 rounded-2xl overflow-hidden">
                        <img src={FEATURE_IMG_2} alt="Stop loss and entries" className="w-full h-64 object-cover opacity-80" />
                        <div className="p-6">
                            <h3 className="text-xl font-semibold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>Precise Risk Management</h3>
                            <p className="text-sm text-zinc-400">AI-calculated stop losses and multiple take profit levels.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pricing */}
            <section className="py-24 px-6 border-t border-white/10" data-testid="pricing-section">
                <div className="max-w-xl mx-auto text-center">
                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-3">Pricing</p>
                    <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        Simple, Transparent Pricing
                    </h2>
                    <p className="text-sm text-zinc-400 mb-12">Start with a 3-day free trial. Cancel anytime.</p>

                    <div className="border border-[#007AFF]/30 bg-[#121212] p-8 text-left">
                        <div className="flex items-baseline gap-1 mb-2">
                            <span className="text-5xl font-black text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>$19</span>
                            <span className="text-zinc-500 text-sm">/month</span>
                        </div>
                        <p className="text-sm text-zinc-400 mb-8">Full access to all features</p>
                        <ul className="space-y-3 mb-8">
                            {["AI-powered entries, SL & TP", "All instruments (Stocks, Forex, Crypto, Futures)", "Real-time price charts", "Unlimited analyses", "Analysis history"].map((item, i) => (
                                <li key={i} className="flex items-center gap-3 text-sm text-zinc-300">
                                    <div className="w-1.5 h-1.5 bg-[#007AFF]" />
                                    {item}
                                </li>
                            ))}
                        </ul>
                        <Link to="/register" className="block">
                            <Button className="w-full bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-full py-6 text-base font-bold" data-testid="pricing-cta-btn">
                                Start Free Trial
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-white/10 py-8 px-6">
                <div className="max-w-6xl mx-auto">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
                        <div className="flex items-center gap-2">
                            <BarChart3 className="w-5 h-5 text-[#007AFF]" />
                            <span className="text-sm font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>TradeNalysis</span>
                        </div>
                        <div className="flex items-center gap-6">
                            <Link to="/terms" className="text-xs text-zinc-500 hover:text-white transition-colors" data-testid="footer-terms">Terms of Service</Link>
                            <Link to="/privacy" className="text-xs text-zinc-500 hover:text-white transition-colors" data-testid="footer-privacy">Privacy Policy</Link>
                            <Link to="/refund" className="text-xs text-zinc-500 hover:text-white transition-colors" data-testid="footer-refund">Refund Policy</Link>
                        </div>
                    </div>
                    <div className="border-t border-white/10 pt-4 flex flex-col md:flex-row items-center justify-between gap-2">
                        <p className="text-xs text-zinc-600">&copy; 2026 TradeNalysis. All rights reserved.</p>
                        <p className="text-xs text-zinc-600">Trading involves risk. Past performance is not indicative of future results.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
}
